import { Eraser, Trash2, Undo2 } from 'lucide-react';
import IconButton from '../board-grid/IconButton';
import {
  MAX_ROUTE_CORRIDOR,
  MAX_ROUTE_THICKNESS,
  MIN_ROUTE_CORRIDOR,
  MIN_ROUTE_THICKNESS,
} from '../route-line';
import type { PhotoLineController } from './usePhotoLine';

/** The words this row wears — see PhotoHoldLabels for why the page supplies them. */
export interface PhotoLineLabels {
  points: (count: number) => string;
  thickness: string;
  corridor: string;
  corridorHint: string;
  deletePoint: string;
  undoPoint: string;
  clearLine: string;
  hint: string;
}

interface PhotoLineControlsProps {
  editor: PhotoLineController;
  labels: PhotoLineLabels;
  /** Offer the corridor. Off on rock, where the line is the whole topo. */
  allowCorridor?: boolean;
  /**
   * Offer the line's thickness. Off where it belongs to the picture rather than
   * to the route — a crag's sector owns it, so a dozen lines on one cliff read
   * as one topo instead of as a dozen people's handwriting.
   */
  allowThickness?: boolean;
  /** Whether to spell the gestures out under the row. */
  showHint?: boolean;
  children?: React.ReactNode;
}

/**
 * How the line is drawn and how it is taken back: its width, the band of wall
 * left lit around it, and the three ways a point goes away.
 */
export function PhotoLineControls({
  editor,
  labels,
  allowCorridor = true,
  allowThickness = true,
  showHint = true,
  children,
}: PhotoLineControlsProps) {
  const { points, corridor, thickness, selected, setCorridor, setThickness, deleteSelected, undo, clearLine } =
    editor;

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{labels.points(points.length)}</p>

      {allowThickness && (
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <label htmlFor="photo-line-thickness" className="block text-xs text-muted-foreground">
              {labels.thickness}
            </label>
            <span className="text-xs tabular-nums text-muted-foreground">
              {(thickness * 100).toFixed(1)}%
            </span>
          </div>
          <input
            id="photo-line-thickness"
            type="range"
            min={MIN_ROUTE_THICKNESS}
            max={MAX_ROUTE_THICKNESS}
            step={0.001}
            value={thickness}
            onChange={(event) => setThickness(Number(event.target.value))}
            disabled={points.length === 0}
            className="w-full accent-primary"
          />
        </div>
      )}

      {allowCorridor && (
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <label htmlFor="photo-line-corridor" className="block text-xs text-muted-foreground">
              {labels.corridor}
            </label>
            {/* The band it sets is a soft edge on a photograph, so a step or two
                of the slider can be hard to be sure of. The number says the
                control is live even when the picture is ambiguous. */}
            <span className="text-xs tabular-nums text-muted-foreground">
              {Math.round(corridor * 100)}%
            </span>
          </div>
          <input
            id="photo-line-corridor"
            type="range"
            min={MIN_ROUTE_CORRIDOR}
            max={MAX_ROUTE_CORRIDOR}
            step={0.01}
            value={corridor}
            onChange={(event) => setCorridor(Number(event.target.value))}
            disabled={points.length === 0}
            className="w-full accent-primary"
          />
          <p className="text-xs text-muted-foreground">{labels.corridorHint}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {/* The line is corrected where it is wrong rather than only from its far
            end, so the point that was pressed is the one this removes. */}
        <IconButton label={labels.deletePoint} onClick={deleteSelected} disabled={selected === null}>
          <Trash2 className="size-4" aria-hidden />
        </IconButton>
        <IconButton label={labels.undoPoint} onClick={undo} disabled={points.length === 0}>
          <Undo2 className="size-4" aria-hidden />
        </IconButton>
        <IconButton label={labels.clearLine} onClick={clearLine} disabled={points.length === 0}>
          <Eraser className="size-4" aria-hidden />
        </IconButton>
        {children}
      </div>

      {showHint && <p className="text-xs text-muted-foreground">{labels.hint}</p>}
    </div>
  );
}
